/**
 * Estado de sincronizacao visto pela interface.
 *
 * Duas responsabilidades, deliberadamente separadas:
 *
 *  1. **Dizer a verdade, sempre.** A contagem de escritas por enviar e de
 *     conflitos por rever vem da base de dados local, nao do motor de
 *     sincronizacao. Assim o cabecalho continua honesto mesmo quando o motor nao
 *     esta ligado, falhou a arrancar, ou o pacote `@flos/sync` ainda nem existe.
 *     Ela nunca pode ficar sem saber se o que escreveu esta salvo.
 *
 *  2. **Delegar o trabalho real** ao `SyncEngine` quando ele existe e ha
 *     backend configurado.
 *
 * O motor nunca esta no caminho critico do primeiro pintado (regra 9). Por isso
 * o controlador nasce sem motor e recebe-o mais tarde, em `attach()`: a casca ja
 * esta desenhada e ja aceita escritas quando o motor entra ao servico.
 */
import { CoreEvents, type ConflictReport, type SyncStatus } from '@flos/shared';
import type { FlosCore } from '@flos/core';
import type {
  ConflictResolutionChoice,
  StoredOutboxItemLike,
  SyncEngineLike,
} from './ports';

export interface ConflictForReview extends ConflictReport {
  id: string;
  detectedAt: string;
}

export interface SyncController {
  status(): SyncStatus;
  /** Ouve alteracoes de estado. Devolve a funcao para deixar de ouvir. */
  subscribe(listener: (status: SyncStatus) => void): () => void;
  syncNow(): Promise<SyncStatus>;
  listConflicts(): Promise<ConflictForReview[]>;
  resolveConflict(id: string, resolution: ConflictResolutionChoice): Promise<void>;
  /** `false` quando ainda nao ha backend configurado — a interface diz isso. */
  readonly connected: boolean;
  /** Motivo, em portugues, de a sincronizacao estar parada. `null` se esta a andar. */
  readonly dormantMessage: string | null;
  /** Escritas parqueadas a espera de decisao humana. Nunca sao apagadas. */
  parked(): Promise<StoredOutboxItemLike[]>;
  /** Liga o motor depois do arranque. Chamado uma vez, na fase diferida. */
  attach(engine: SyncEngineLike | null, dormantMessage?: string | null): void;
  refresh(): Promise<void>;
  stop(): void;
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

export function createSyncController(
  core: FlosCore,
  initialEngine: SyncEngineLike | null,
): SyncController {
  const listeners = new Set<(status: SyncStatus) => void>();
  let engine: SyncEngineLike | null = initialEngine;
  let dormantMessage: string | null = null;
  let current: SyncStatus = {
    phase: isOnline() ? 'idle' : 'offline',
    pending: 0,
    conflicts: 0,
    lastSyncAt: null,
    lastError: null,
    online: isOnline(),
  };
  let stopped = false;

  function publish(next: SyncStatus): void {
    current = next;
    for (const listener of listeners) {
      try {
        listener(next);
      } catch (error) {
        core.logger.error('Ouvinte do estado de sincronizacao falhou', {
          erro: error instanceof Error ? error.message : String(error),
        });
      }
    }
  }

  /**
   * Le as contagens reais do disco.
   *
   * Os filtros nao sao um detalhe: uma escrita parqueada ja nao esta "por
   * enviar" (ninguem a vai tentar sozinho) e um conflito ja decidido ja nao
   * precisa dela. Contá-los mostrava-lhe um numero que nunca descia.
   */
  async function refresh(): Promise<void> {
    if (stopped) return;
    let pending = 0;
    let conflicts = 0;
    try {
      pending = await core.db.outbox
        .filter((item) => (item as StoredOutboxItemLike).poisoned !== true)
        .count();
      conflicts = await core.db.conflicts.filter((row) => !row.resolvedAt).count();
    } catch (error) {
      core.logger.warn('Nao consegui contar a fila de sincronizacao', {
        erro: error instanceof Error ? error.message : String(error),
      });
    }

    const engineStatus = engine?.status();
    const online = isOnline();
    publish({
      phase: !online ? 'offline' : (engineStatus?.phase ?? 'idle'),
      pending,
      conflicts,
      lastSyncAt: engineStatus?.lastSyncAt ?? current.lastSyncAt,
      lastError: engineStatus?.lastError ?? null,
      online,
    });
  }

  // Uma escrita local muda a contagem por enviar: e o momento em que o
  // cabecalho tem de mudar, senao ela ve "0 por enviar" logo depois de escrever.
  const offRecord = core.events.on(CoreEvents.RECORD_CHANGED, () => {
    void refresh();
  });
  const offStatus = core.events.on(CoreEvents.SYNC_STATUS_CHANGED, () => {
    void refresh();
  });
  const offConflict = core.events.on(CoreEvents.SYNC_CONFLICT, () => {
    void refresh();
  });

  const onConnectivity = (): void => {
    core.events.emit(CoreEvents.CONNECTIVITY_CHANGED, { online: isOnline() });
    void refresh();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onConnectivity);
    window.addEventListener('offline', onConnectivity);
  }

  void refresh();

  return {
    get connected() {
      return engine !== null;
    },
    get dormantMessage() {
      return engine === null ? dormantMessage : null;
    },
    status: () => current,
    subscribe(listener) {
      listeners.add(listener);
      listener(current);
      return () => listeners.delete(listener);
    },

    attach(next, message = null) {
      engine = next;
      dormantMessage = message;
      if (next) {
        // `start()` liga os ouvintes de rede/visibilidade e faz uma primeira
        // volta. Ja estamos fora do caminho critico, portanto pode ir a rede.
        try {
          next.start();
        } catch (error) {
          core.logger.error('O motor de sincronizacao nao arrancou', {
            erro: error instanceof Error ? error.message : String(error),
          });
        }
      }
      void refresh();
    },

    async syncNow() {
      if (!engine) {
        // Sem backend nao ha nada a fazer, mas ha que dizer-lho por palavras.
        await refresh();
        return {
          ...current,
          lastError: dormantMessage ?? 'Ainda não há servidor configurado.',
        };
      }
      try {
        await engine.syncNow();
      } catch (error) {
        core.logger.error('Sincronizacao manual falhou', {
          erro: error instanceof Error ? error.message : String(error),
        });
      }
      await refresh();
      return current;
    },

    async listConflicts() {
      if (engine) {
        try {
          return (await engine.listConflicts()) as ConflictForReview[];
        } catch (error) {
          core.logger.warn('O motor nao devolveu conflitos; leio da base local', {
            erro: error instanceof Error ? error.message : String(error),
          });
        }
      }
      const rows = await core.db.conflicts.toArray();
      return rows
        .filter((row) => !row.resolvedAt)
        .map((row) => ({ ...row, id: row.outboxItemId }));
    },

    async resolveConflict(id, resolution) {
      if (engine) {
        await engine.resolveConflict(id, resolution);
      } else {
        // Sem motor so podemos arquivar a decisao; fica registrada e o motor
        // aplica-a quando existir. Nunca apagamos o conflito sem decisao humana.
        const row = await core.db.conflicts.get(id);
        if (row) {
          await core.db.conflicts.put({ ...row, resolvedAt: new Date().toISOString() });
        }
        core.logger.warn('Conflito decidido sem motor de sincronizacao ligado', {
          conflito: id,
          decisao: resolution.kind,
        });
      }
      await refresh();
    },

    /**
     * Escritas que o servidor recusou vezes de mais. Saem da fila de envio mas
     * NUNCA sao apagadas — ficam a espera de alguem olhar para elas.
     */
    async parked() {
      const rows = (await core.db.outbox.toArray()) as StoredOutboxItemLike[];
      return rows.filter((item) => item.poisoned === true);
    },

    refresh,
    stop() {
      stopped = true;
      offRecord();
      offStatus();
      offConflict();
      listeners.clear();
      engine?.stop();
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onConnectivity);
        window.removeEventListener('offline', onConnectivity);
      }
    },
  };
}
